import React, { useState, useEffect, useContext } from "react";
import { BasePageModal } from "@/shared/components/modal/page-modal/base-page-modal";
import { OneTimePassModal } from "@/features/accounts/payment/scraping/components/one-time-pass-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useViewTrades } from "@/features/accounts/shared/hooks/use-view-trades";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useOneTimePassContext } from "@/features/accounts/payment/scraping/contexts/one-time-pass-context";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { getJSTDateString } from "@/shared/utils/date";

interface Props {
  accountGroupId: string;
  mlinkAccountId: string;
  mlinkId: string;
  passbookType: string;
  scrapeType: number;
  otpYn: string;
  onTapCompleteModal: () => void;
  onTapCloseModal: () => void;
}

const UPDATE_BUTTON_ID = "update-trades-button";

// 明細更新モーダル
export const UpdateTradeModal = ({ accountGroupId, mlinkAccountId, mlinkId, passbookType, scrapeType, otpYn, onTapCompleteModal, onTapCloseModal }: Props) => {
  const { updateTrades } = useViewTrades();
  const {
    openHomeModal,
  } = useModalContext();
  const {
    resetOtpContext,
  } = useOneTimePassContext();
  const {
    setSearchStartDate,
    setSearchEndDate,
    setStartDateForce,
    setEndDateForce,
    setForceTradesSetDone,
  } = useTradesContext();
  const loadingCtx = useContext(LoadingModalContext);
  const [startDate, setStartDate] = useState<string>(getJSTDateString(new Date(), -1));
  const [endDate, setEndDate] = useState<string>(getJSTDateString());
  const [dateError, setDateError] = useState("");
  // OTPが発生しうる口座のポーリング待機中フラグ
  const [isWaiting, setIsWaiting] = useState(false);

  const formatDate = (dateStr: string) => dateStr.replace(/-/g, "");

  // 今日の日付（JST基準、YYYY-MM-DD形式）を取得
  const today = getJSTDateString();

  // OTPが発生しうる口座かどうか（サーバ版スクレイピング、かつOTP設定あり）
  const isOtpAccount = scrapeType === 2 && otpYn !== "0";

  useEffect(() => {
    if (startDate === "" || endDate === "") {
      setDateError("開始日と終了日を両方選択してください。");
    } else if (startDate > endDate) {
      setDateError("開始日は終了日より前の日付を選択してください。");
    } else {
      setDateError("");
    }
  }, [startDate, endDate]);

  // OTPアカウントのポーリング状態を監視し、OTP検知・処理完了時に適切な制御を行う
  useEffect(() => {
    if (!isWaiting) return;
    if (openHomeModal === "UpdateTradesOtp") {
      // OTP検知：OTPモーダルのボタンが操作できるようローディングを解除
      loadingCtx.setIsLoading(false);
      return;
    }
    if (openHomeModal !== "UpdateTrades") {
      // 処理完了（ポーリング終了によりopenHomeModalがundefinedになった場合）
      setIsWaiting(false);
      loadingCtx.setIsLoading(false);
      onTapCompleteModal();
    }
  }, [openHomeModal, isWaiting]);

  // 「更新」ボタンのクリックハンドラ
  const handleUpdateClick = async () => {
    if (isWaiting) return;
    setDateError("");
    setSearchStartDate(startDate.replace(/-/g, ""));
    setSearchEndDate(endDate.replace(/-/g, ""));
    setStartDateForce(startDate.replace(/-/g, ""));
    setEndDateForce(endDate.replace(/-/g, ""));
    setForceTradesSetDone(false);
    if (!isOtpAccount) {
      // OTPが発生しない口座は即座にモーダルを閉じる
      // ポーリング完了後のrefreshはview-trades側で行う
      updateTrades(accountGroupId, mlinkAccountId, mlinkId, passbookType, scrapeType, formatDate(startDate), formatDate(endDate));
      onTapCompleteModal();
      return;
    }
    // OTPが発生しうる口座：APIレスポンスを待ってからポーリング待機状態へ
    setIsWaiting(true);
    await updateTrades(accountGroupId, mlinkAccountId, mlinkId, passbookType, scrapeType, formatDate(startDate), formatDate(endDate));
    // updateTrades内のfinally処理でisLoading=falseになるため、ポーリング待機中もローディングを維持する
    loadingCtx.setIsLoading(true);
    loadingCtx.setActiveButtonId(UPDATE_BUTTON_ID);
  };

  // モーダルのクローズ処理（ローディング・待機状態をリセットしてから閉じる）
  const handleClose = () => {
    setIsWaiting(false);
    loadingCtx.setIsLoading(false);
    onTapCloseModal();
  };

  return (
    <>
    {openHomeModal === "UpdateTrades" && (
      <BasePageModal
        title="明細を更新"
        className="md-list-reload"
        handleClose={handleClose}
        bottomChildren={
          <BaseRoundButton
            buttonText="更新"
            id={UPDATE_BUTTON_ID}
            onClick={handleUpdateClick}
            isActive={dateError === ""}
            loadingNegative={true}
          />
        }
      >
        <h2>照会する期間を指定</h2>
        <dl className="period">
          <dd>
            <input type="date" id="startDate" value={startDate} max={today} onChange={(e) => setStartDate(e.target.value)} />
            <span>から</span>
            <input type="date" id="endDate" value={endDate} max={today} onChange={(e) => setEndDate(e.target.value)} />
          </dd>
          {dateError && <p className="pass_error">{dateError}</p>}
        </dl>
      </BasePageModal>
    )}
    {openHomeModal === "UpdateTradesOtp" && (
      <OneTimePassModal
        processType={"updTrades"}
        onTapCloseModal={() => {
          resetOtpContext();
          setIsWaiting(false);
          loadingCtx.setIsLoading(false);
          onTapCloseModal();
        }}
        onTapCompleteModal={async (newOtpInfo?: string) => {
          await updateTrades(
            accountGroupId,
            mlinkAccountId,
            mlinkId,
            passbookType,
            scrapeType,
            formatDate(startDate),
            formatDate(endDate),
            newOtpInfo
          );
        }}
      />
    )}
    </>
  );
};